/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: /home/salvo/Desktop/collegamenti_utili/software_dev/workspace/coronatek-v2/frontend/src/components/modular-form/custom/DeliveryMethodsCustom.tsx
 * Release: V2.0.0 - Modalità di Consegna per Form Modulare
 * Descrizione: Componente custom per modalità di consegna integrato nel sistema modulare
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, 
  Home,
  Package,
  Zap,
  MapPin,
  Building
} from 'lucide-react';

interface DeliveryMethodsCustomProps {
  value?: any;
  onChange?: (value: any) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const DELIVERY_OPTIONS = [
  { id: 'pickup', label: 'Ritiro in officina', icon: Building },
  { id: 'courier', label: 'Corriere preferito', icon: Truck },
  { id: 'company_delivery', label: 'Consegna con nostro mezzo', icon: Home },
  { id: 'customer_pickup', label: 'Ritiro presso cliente', icon: MapPin },
  { id: 'express', label: 'Corriere express', icon: Zap },
  { id: 'special', label: 'Trasporto speciale', icon: Package },
  { id: 'pickup_point', label: 'Punto di ritiro convenzionato', icon: MapPin }
];

export const DeliveryMethodsCustom: React.FC<DeliveryMethodsCustomProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  // Componente sempre espanso - non serve più lo stato
  
  // DEBUG LOG
  console.log('🚚 [DeliveryMethodsCustom] Value ricevuto:', value);
  
  // COMPATIBILITÀ: Gestisce sia array semplici che oggetti complessi
  const [selectedMethods, setSelectedMethods] = useState<string[]>(() => {
    if (Array.isArray(value)) {
      console.log('🚚 [DeliveryMethodsCustom] Value è array:', value);
      return value; // Array semplice
    } else if (value?.methods && Array.isArray(value.methods)) {
      console.log('🚚 [DeliveryMethodsCustom] Value è oggetto con methods:', value.methods);
      return value.methods; // Oggetto complesso legacy
    }
    console.log('🚚 [DeliveryMethodsCustom] Value non valido, usando default []');
    return []; // Default
  });
  
  const [preferences, setPreferences] = useState(() => {
    if (value?.preferences && typeof value.preferences === 'object') {
      console.log('🚚 [DeliveryMethodsCustom] Preferences trovate:', value.preferences);
      return value.preferences; // Oggetto complesso legacy
    }
    return {}; // Default
  });

  // Sincronizza lo stato quando il value cambia
  useEffect(() => {
    if (value) {
      console.log('🚚 [DeliveryMethodsCustom] Aggiornamento value:', value);
      if (Array.isArray(value)) {
        setSelectedMethods(value);
      } else if (value?.methods && Array.isArray(value.methods)) {
        setSelectedMethods(value.methods);
        if (value.preferences) {
          setPreferences(value.preferences);
        }
      }
    }
  }, [value]);

  const handleMethodToggle = (methodId: string) => {
    if (disabled) return;

    const newMethods = selectedMethods.includes(methodId)
      ? selectedMethods.filter(id => id !== methodId)
      : [...selectedMethods, methodId];

    setSelectedMethods(newMethods);
    
    // Salva sia i metodi che le preferenze
    onChange?.({
      methods: newMethods,
      preferences,
      address: preferences.preferredAddress || '',
      timeSlot: preferences.timeSlot || '',
      notes: preferences.notes || ''
    });
  };

  const handlePreferenceChange = (key: string, newValue: string) => {
    const newPreferences = {
      ...preferences,
      [key]: newValue
    };
    
    setPreferences(newPreferences);
    
    // Salva sia i metodi che le preferenze aggiornate
    onChange?.({
      methods: selectedMethods,
      preferences: newPreferences,
      address: newPreferences.preferredAddress || '',
      timeSlot: newPreferences.timeSlot || '',
      notes: newPreferences.notes || ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Rimuovo header e mostro direttamente le opzioni */}
      <div className="space-y-4">
            {/* Opzioni di consegna */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {DELIVERY_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedMethods.includes(option.id);
                
                return (
                  <motion.div
                    key={option.id}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-zinc-600 bg-zinc-700/50 hover:border-zinc-500'
                      }
                      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    onClick={() => handleMethodToggle(option.id)}
                    whileHover={!disabled ? { scale: 1.02 } : {}}
                    whileTap={!disabled ? { scale: 0.98 } : {}}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${
                        isSelected ? 'text-green-400' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        isSelected ? 'text-white' : 'text-gray-300'
                      }`}>
                        {option.label}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Preferenze aggiuntive */}
            {(selectedMethods.length > 0 || preferences.preferredAddress || preferences.timeSlot || preferences.notes) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3 pt-4 border-t border-zinc-600"
              >
                <h4 className="text-sm font-medium text-gray-300">
                  Preferenze e Note
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Indirizzo di consegna preferito"
                    value={preferences.preferredAddress || ''}
                    onChange={(e) => handlePreferenceChange('preferredAddress', e.target.value)}
                    disabled={disabled}
                    className="w-full p-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-gray-400 focus:border-green-500 focus:outline-none"
                  />
                  
                  <input
                    type="text"
                    placeholder="Orari preferiti"
                    value={preferences.timeSlot || ''}
                    onChange={(e) => handlePreferenceChange('timeSlot', e.target.value)}
                    disabled={disabled}
                    className="w-full p-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-gray-400 focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <textarea
                  placeholder="Note aggiuntive sulla consegna..."
                  value={preferences.notes || ''}
                  onChange={(e) => handlePreferenceChange('notes', e.target.value)}
                  disabled={disabled}
                  className="w-full p-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-gray-400 focus:border-green-500 focus:outline-none resize-none"
                  rows={3}
                />
              </motion.div>
            )}
      </div>
    </div>
  );
};

export default DeliveryMethodsCustom;
